import { supabase } from '@/supabase.config';

/**
 * Validates ADB code format: must be in the format "12-123" (number-number)
 */
export function validateAdbCodeFormat(adbCode: string): { valid: boolean; message?: string } {
  if (!adbCode || !adbCode.trim()) {
    return { valid: false, message: 'El código ADB es requerido' };
  }

  const adbPattern = /^\d+-\d+$/;
  if (!adbPattern.test(adbCode.trim())) {
    return { valid: false, message: 'El código ADB debe tener el formato: número-número (ej: 12-123)' };
  }

  return { valid: true };
}

/**
 * Validates CI/DNI format with minimal validation to support international IDs.
 * Accepts alphanumeric characters with common separators (space, dash, slash, period).
 * Examples:
 * - Bolivia: "12345", "12345 AB", "12345-1a AB"
 * - Argentina: "12345678", "12.345.678"
 * - Chile: "12345678-9"
 * - Peru: "12345678"
 * - Brazil: "123.456.789-01", "12345678901"
 */
export function validateCIFormat(ci: string): { valid: boolean; message?: string } {
  if (!ci || !ci.trim()) {
    return { valid: false, message: 'La cédula es requerida' };
  }

  const cleanCI = ci.trim();
  
  // Check length (5-30 characters is reasonable for international IDs)
  if (cleanCI.length < 5 || cleanCI.length > 30) {
    return { 
      valid: false, 
      message: 'La cédula debe tener entre 5 y 30 caracteres' 
    };
  }
  
  // Allow alphanumeric characters and common separators: space, dash, slash, period
  // This covers most international ID formats
  const validPattern = /^[0-9a-zA-Z\s./-]+$/;
  
  if (!validPattern.test(cleanCI)) {
    return { 
      valid: false, 
      message: 'La cédula contiene caracteres no válidos. Use solo letras, números, espacios, guiones, puntos o barras' 
    };
  }

  return { valid: true };
}

/**
 * Normalizes DNI format by converting to uppercase and trimming
 * Preserves the original format structure (separators, etc.)
 */
export function normalizeDNI(dni: string): string {
  if (!dni) return dni;
  
  // Trim and convert to uppercase for consistency
  return dni.trim().toUpperCase();
}

/**
 * Validates email format
 */
export function validateEmail(email: string): { valid: boolean; message?: string } {
  if (!email || !email.trim()) {
    return { valid: true }; // Email is optional
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email.trim())) {
    return { valid: false, message: 'Formato de email inválido' };
  }

  return { valid: true };
}

/**
 * Checks if ADB code already exists in the database (excluding current minister if editing)
 */
export async function checkAdbCodeUniqueness(
  adbCode: string, 
  excludeId?: string
): Promise<{ unique: boolean; message?: string }> {
  if (!adbCode || !adbCode.trim()) {
    return { unique: false, message: 'El código ADB es requerido' };
  }

  try {
    let query = supabase
      .from('ministers')
      .select('id, adb_code')
      .eq('adb_code', adbCode.trim())
      .eq('is_active', true);

    // Exclude current minister when editing
    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error checking ADB code uniqueness:', error);
      return { unique: false, message: 'Error al verificar el código ADB' };
    }

    if (data) {
      return { unique: false, message: `El código ADB ${adbCode} ya está en uso` };
    }

    return { unique: true };
  } catch (error) {
    console.error('Error checking ADB code uniqueness:', error);
    return { unique: false, message: 'Error al verificar el código ADB' };
  }
}

/**
 * Checks if CI/DNI already exists in the database (excluding current minister if editing)
 */
export async function checkCIUniqueness(
  ci: string, 
  excludeId?: string
): Promise<{ unique: boolean; message?: string }> {
  if (!ci || !ci.trim()) {
    return { unique: false, message: 'La cédula es requerida' };
  }

  try {
    let query = supabase
      .from('ministers')
      .select('id, dni')
      .eq('dni', ci.trim())
      .eq('is_active', true);

    // Exclude current minister when editing
    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error checking CI uniqueness:', error);
      return { unique: false, message: 'Error al verificar la cédula' };
    }

    if (data) {
      return { unique: false, message: `La cédula ${ci} ya está en uso` };
    }

    return { unique: true };
  } catch (error) {
    console.error('Error checking CI uniqueness:', error);
    return { unique: false, message: 'Error al verificar la cédula' };
  }
}

/**
 * Generates full name from name parts, trimming and removing extra spaces
 */
export function generateFullName(
  firstName: string | null,
  lastName: string | null,
  secondLastName: string | null,
  marriageLastName: string | null
): string {
  const parts = [
    firstName?.trim(),
    lastName?.trim(),
    secondLastName?.trim(),
    marriageLastName?.trim()
  ].filter(part => part && part.length > 0);

  return parts.join(' ');
}

/**
 * Validates all required fields for minister form
 */
export function validateMinisterForm(formData: {
  adb_code: string;
  first_name: string;
  last_name: string;
  dni: string;
  degree_id: string;
  district_id: string;
  region_id: string;
  email?: string;
}, options?: { skipEcclesialRequired?: boolean }): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  // Validate ADB code format
  const adbValidation = validateAdbCodeFormat(formData.adb_code);
  if (!adbValidation.valid) {
    errors.adb_code = adbValidation.message!;
  }

  // Validate CI format
  const ciValidation = validateCIFormat(formData.dni);
  if (!ciValidation.valid) {
    errors.dni = ciValidation.message!;
  }

  // Validate required text fields
  if (!formData.first_name?.trim()) {
    errors.first_name = 'El nombre es requerido';
  }

  if (!formData.last_name?.trim()) {
    errors.last_name = 'El apellido es requerido';
  }

  // Validate required ID fields (skipped in edit mode — managed via action dialogs)
  if (!options?.skipEcclesialRequired) {
    if (!formData.degree_id) {
      errors.degree_id = 'El grado es requerido';
    }

    if (!formData.district_id) {
      errors.district_id = 'El distrito es requerido';
    }

    if (!formData.region_id) {
      errors.region_id = 'La región es requerida';
    }
  }

  // Validate email if provided
  if (formData.email) {
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      errors.email = emailValidation.message!;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}
